// react
import { useContext } from 'react';

// types
import {
	AccountType,
	EntityType,
	Financier,
	StatsProjectsBreakdown,
	TransactionType,
	UserRole,
} from '../../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextDashboard } from '../Dashboard.context';

// mui
import { Box, Grid, Paper, Typography } from '@mui/material';

// ui
import { Empty } from '../../../ui/Empty/Empty';
import { FlagText } from '../../../ui/FlagText/FlagText';
import { IconCircle } from '../../../ui/IconCircle/IconCircle';
import { IconCircleLoan } from '../../../ui/IconCircleLoan/IconCircleLoan';
import {
	accountIconTypes,
	entityIconTypes,
	transactionIconTypes,
} from '../../../ui/IconCircle/IconCircle.utils';
import { Table, TableHeader } from '../../../ui/Table/Table';
import { TitleBar } from '../../../ui/TitleBar/TitleBar';

// utils
import {
	translateAccountType,
	translateRole,
	translateTransactionType,
	translateType,
} from '../../../../utils/translations';

// props
interface BreakdownProps {
	type: 'accounts' | 'contracts' | 'entities' | 'loans' | 'projects' | 'transactions' | 'users';
}

export const Breakdown = ({ type }: BreakdownProps) => {
	// context
	const { isLoadingResource, statsResource } = useContext(ContextDashboard);

	// hooks
	const { t } = useTranslation();

	// vars

	const empty = {
		content: {
			children: t('empty.default.content'),
		},
		title: {
			children: t('empty.default.title'),
		},
	};
	let headers: TableHeader[] = [];
	let data: unknown[] = [];

	if (statsResource) {
		const resource = statsResource[type];

		if (resource) {
			switch (type) {
				case 'accounts':
					empty.content = { children: t('empty.dashboard.accounts.breakdown.content') };
					empty.title = { children: t('empty.dashboard.accounts.breakdown.title') };
					headers = [
						{
							label: t('type'),
							render: (value: AccountType) => {
								console.log('Value:', value);
								return (
									<Grid alignItems="center" container spacing={1}>
										<Grid item>
											<IconCircle size="sm" type={accountIconTypes[value]} />
										</Grid>
										<Grid item>
											<Typography fontWeight={600}>{translateAccountType(value, t)}</Typography>
										</Grid>
									</Grid>
								);
							},
							value: 'type',
						},
						{ align: 'right', label: t('total'), value: 'total' },
					];
					data = resource.breakdown.sort((a, b) => b.total - a.total);
					break;
				case 'contracts':
					empty.content = { children: t('empty.dashboard.contracts.breakdown.content') };
					empty.title = { children: t('empty.dashboard.contracts.breakdown.title') };
					headers = [
						{
							label: t('currency'),
							render: (value: string) => {
								return <FlagText currency={value} TypographyProps={{ fontWeight: 600 }} />;
							},
							value: 'currency',
						},
						{ align: 'right', label: t('total'), value: 'total' },
					];
					data = resource.breakdown.sort((a, b) => b.total - a.total);
					break;
				case 'entities':
					empty.content = { children: t('empty.dashboard.entities.breakdown.content') };
					empty.title = { children: t('empty.dashboard.entities.breakdown.title') };
					headers = [
						{
							label: t('type'),
							render: (value: EntityType) => {
								return (
									<Grid alignItems="center" container spacing={1}>
										<Grid item>
											<IconCircle size="sm" type={entityIconTypes[value]} />
										</Grid>
										<Grid item>
											<Typography fontWeight={600}>{translateType(value, t)}</Typography>
										</Grid>
									</Grid>
								);
							},
							value: 'type',
						},
						{ align: 'right', label: t('total'), value: 'total' },
					];
					data = resource.breakdown.sort((a, b) => b.total - a.total);
					break;
				case 'loans':
					empty.content = { children: t('empty.dashboard.loans.breakdown.content') };
					empty.title = { children: t('empty.dashboard.loans.breakdown.title') };
					headers = [
						{
							label: t('financier'),
							render: (value: Financier) => {
								return (
									<Grid alignItems="center" container spacing={1}>
										<Grid item>
											<IconCircleLoan financier={value} size="sm" />
										</Grid>
										<Grid item>
											<Typography fontWeight={600}>{value}</Typography>
										</Grid>
									</Grid>
								);
							},
							value: 'financier',
						},
						{ align: 'right', label: t('total'), value: 'total' },
					];
					data = resource.breakdown.sort((a, b) => b.total - a.total);
					break;
				case 'projects':
					empty.content = { children: t('empty.dashboard.projects.breakdown.content') };
					empty.title = { children: t('empty.dashboard.projects.breakdown.title') };
					headers = [
						{
							label: t('country'),
							render: (value: string, header: TableHeader, row: StatsProjectsBreakdown) => {
								return <FlagText country={row.country} TypographyProps={{ fontWeight: 600 }} />;
							},
							value: 'country',
						},
						{ align: 'right', label: t('total'), value: 'total' },
					];
					data = resource.breakdown.sort((a, b) => b.total - a.total);
					break;
				case 'transactions':
					empty.content = { children: t('empty.dashboard.transactions.breakdown.content') };
					empty.title = { children: t('empty.dashboard.transactions.breakdown.title') };
					headers = [
						{
							label: t('type'),
							render: (value: TransactionType) => {
								return (
									<Grid alignItems="center" container spacing={1}>
										<Grid item>
											<IconCircle size="sm" type={transactionIconTypes[value]} />
										</Grid>
										<Grid item>
											<Typography fontWeight={600}>{translateTransactionType(value, t)}</Typography>
										</Grid>
									</Grid>
								);
							},
							value: 'type',
						},
						{ align: 'right', label: t('total'), value: 'total' },
					];
					data = resource.breakdown.sort((a, b) => b.total - a.total);
					break;
				case 'users':
					empty.content = { children: t('empty.dashboard.users.breakdown.content') };
					empty.title = { children: t('empty.dashboard.users.breakdown.title') };
					headers = [
						{
							label: t('role'),
							render: (value: UserRole) => {
								return (
									<Grid alignItems="center" container spacing={1}>
										<Grid item>
											<IconCircle size="sm" type="user" />
										</Grid>
										<Grid item>
											<Typography fontWeight={600}>{translateRole(value, t)}</Typography>
										</Grid>
									</Grid>
								);
							},
							value: 'role',
						},
						{ align: 'right', label: t('total'), value: 'total' },
					];
					data = resource.breakdown.sort((a, b) => b.total - a.total);
					break;
			}
		}
	}

	let content = (
		<Empty
			content={empty.content}
			hasContent={!isLoadingResource}
			hasTitle={!isLoadingResource}
			isLoading={isLoadingResource}
			position="absolute"
			title={empty.title}
		/>
	);

	if (data.length > 0) {
		content = <Table data={data} frame="DEFAULT" headers={headers} sxBox={{ borderRadius: 2 }} />;
	}

	return (
		<Paper sx={{ position: 'relative', height: '100%' }}>
			<Grid container direction="column" sx={{ height: '100%' }}>
				<Grid item>
					<TitleBar
						BoxProps={{ sx: { borderTopLeftRadius: 8, borderTopRightRadius: 8 } }}
						title={{ children: t('breakdown') }}
					/>
				</Grid>
				<Grid flex={1} item>
					<Box
						sx={{
							position: 'relative',
							height: {
								lg: 340,
							},
							overflowY: 'auto',
						}}>
						{content}
					</Box>
				</Grid>
			</Grid>
		</Paper>
	);
};
